import type { CarePlan, Reference } from '@medplum/fhirtypes'
import { CarePlanState } from '@seen/core/models/care-plans/constants'
import { createCarePlanSignatures } from '@seen/core/models/care-plans/transactions'
import {
  CARE_TEAM_CATEGORIES,
  idtRoles,
} from '@seen/core/models/care-teams/constants'
import { IDT } from '@seen/core/models/care-teams/idt'
import { SignatureTask } from '@seen/core/models/tasks/signature/signatureTask'
import { Role } from '@seen/core/roles'
import { Button } from '@seen/ui'
import { IconArrowLeft, IconSignature } from '@tabler/icons-react'
import type { JSX } from 'react'
import { useNavigate, useParams } from 'react-router'
import { CarePlanHeader } from '#src/components/care-plan/CarePlanHeader.tsx'
import { ActivateCarePlanButton } from '#src/components/care-plan/signatures/ActivateCarePlanButton.tsx'
import { CarePlanParticipantReview } from '#src/components/care-plan/signatures/CarePlanParticipantReview.tsx'
import { useCarePlanData } from '#src/components/care-plan/useCarePlanData.ts'
import { Loader } from '#src/components/core/Loader.tsx'
import { SignatureTable } from '#src/components/signature/SignatureTable.tsx'
import { useCurrentParticipant } from '#src/contexts/current-participant/useCurrentParticipant.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useBatchMutation } from '#src/hooks/useFhirBundleMutation.ts'
import { usePortalStore } from '#src/hooks/usePortalStore.ts'
import { useSearchOne } from '#src/hooks/useSearchOne.ts'

export function CarePlanSignaturesPage(): JSX.Element {
  const navigate = useNavigate()
  const { carePlanId } = useParams()
  const participant = useCurrentParticipant()
  const practitioner = useCurrentPractitioner()
  const showAdminActions = usePortalStore((state) => state.showAdminActions)

  const { isLoading, carePlan, signatures } = useCarePlanData(
    participant.reference,
    {
      reference: `CarePlan/${carePlanId}`,
    } satisfies Reference<CarePlan>
  )

  const careTeamQuery = useSearchOne(IDT, {
    subject: participant.reference,
    category: CARE_TEAM_CATEGORIES.IDT,
  })

  const requestSignaturesMutation = useBatchMutation()

  const idt = careTeamQuery.data

  // Signatures are one-per-IDT-discipline, deduped on performerType. Offer the
  // request whenever some discipline still lacks a task; createCarePlanSignatures
  // is idempotent per role (ifNoneExist), so re-requesting only fills the gaps
  // without duplicating disciplines already present (including on legacy plans).
  const coveredDisciplines = new Set(
    signatures.map((signature) => signature.role).filter(Boolean)
  )
  const needsSignatureRequest = coveredDisciplines.size < idtRoles.length

  const handleRequestSignatures = (): void => {
    if (!carePlan || !idt) {
      return
    }
    requestSignaturesMutation.mutate((batch) => {
      /* Uses a batch (not transaction) to avoid Medplum's strict isolation limit
       * for large IDT teams (11+ members). This is safe because the state change
       * is idempotent and createCarePlanSignatures uses ifNoneExist to prevent
       * duplicate tasks. Will be replaced with event-driven subscription. */
      batch.patch(carePlan, (cp) => {
        cp.state = CarePlanState.PendingSignatures
      })
      createCarePlanSignatures(batch, {
        carePlan,
        idt,
        requester: practitioner.reference,
      })
    })
  }

  if (isLoading || !carePlan) {
    return <Loader />
  }

  return (
    <div>
      <div>
        <CarePlanHeader carePlan={carePlan} activitiesPath="../activities" />
        <div className="mt-10 mb-8 grid grid-cols-3 items-center gap-4">
          <Button onClick={() => navigate(`..`)} variant="outline">
            <IconArrowLeft size={16} className="mr-2" />
            Back to Care Plan
          </Button>
        </div>

        <h2>IDT Convened</h2>

        <div className="flex flex-col gap-4 pt-4">
          {needsSignatureRequest ? (
            <div>
              {(showAdminActions || practitioner.isRole(Role.CM)) && (
                <Button
                  onClick={handleRequestSignatures}
                  variant="default"
                  disabled={!idt}
                  loading={requestSignaturesMutation.isPending}
                >
                  <IconSignature className="mr-2 size-4" /> Request{' '}
                  {idtRoles.length} signatures
                </Button>
              )}
            </div>
          ) : (
            <p className="text-md">
              {`${SignatureTask.completedRatio(signatures)} signatures completed.`}
            </p>
          )}
          <SignatureTable
            signatures={signatures}
            dateFormat="MM/dd/yy"
            showDiscipline
          />
          <CarePlanParticipantReview carePlan={carePlan} />

          <div>
            <ActivateCarePlanButton
              carePlan={carePlan}
              signatures={signatures}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
